import _ from 'lodash';
import * as ActionTypes from '../actions/types/SeatingChartActionTypes';

const initialState = {
  busy: false,
  byEventId: {},
  events: [],
  seatingChart: {},
  tablesByUuid: {},
  tables: [],
  totalNumSeats: 0,
  seatsByGuestDistinction: {},
  addTableData: {},
  onboarding: false,
  step: 0,
  seatsReorderedTable: null,
  event: {},
  updateSeatsRequest: [],
  selectedGuests: { guests: [], couple: [] },
  draggingSeatInProgress: false,
  draggingTableInProgress: false,
  hoveredTable: null
};

const chartReducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.REQUEST_CHART: {
      return { ...state, busy: true };
    }
    case ActionTypes.RECEIVE_CHART: {
      const seatingChart = action.payload.data;
      const seatingChartTables = seatingChart.tables;
      const tablesByUuid = {};
      const tables = [];
      const seatsByGuestDistinction = { guests: {}, couple: {} };
      let totalNumSeats = 0;
      // Update any empty seats, calculate the number of filled seats, and populate our seat -> guest distinction map
      if (seatingChartTables.length > 0) {
        seatingChartTables.forEach((table) => {
          const newTable = table;
          const seats = table.seats;
          totalNumSeats += seats.length;
          let numFilledSeats = 0;
          if (seats.length > 0) {
            seats.map((seat) => {
              const newSeat = seat;
              newSeat.isEmpty = false;
              if (seat.couple_role != null) {
                seatsByGuestDistinction.couple[newSeat.couple_role] = newSeat;
                numFilledSeats += 1;
              } else if (seat.guest_id != null) {
                seatsByGuestDistinction.guests[newSeat.guest_id] = newSeat;
                numFilledSeats += 1;
              } else {
                newSeat.isEmpty = true;
              }
              return newSeat;
            });
          }
          newTable.num_filled_seats = numFilledSeats;
          newTable.seats = seats;
          tablesByUuid[table.uuid] = { table, index: tables.length };
          tables.push(newTable);
        });
      }
      return Object.assign(
        {},
        state,
        { busy: false, seatingChart, tablesByUuid, tables, seatsReorderedTable: null, seatsByGuestDistinction, updateSeatsRequest: [], totalNumSeats }
      );
    }
    case ActionTypes.REQUEST_CHARTS: {
      return { ...state, busy: true };
    }
    case ActionTypes.RECEIVE_CHARTS: {
      const events = [];
      const seatingCharts = action.payload;
      const byEventId = seatingCharts.event_to_charts_map;
      Object.keys(byEventId).forEach((key) => {
        events.push({ event: key, uuid: byEventId[key][0].uuid, name: byEventId[key][0].name });
      });
      return Object.assign(
        {},
        state,
        { busy: false },
        { byEventId, events }
      );
    }
    case ActionTypes.REORDER_TABLES: {
      const tables = state.tables.slice();
      if (tables.length <= 0) {
        return Object.assign(
          {},
          state,
          { busy: false }
        );
      }

      const sourceIndex = action.payload.sourceIndex;
      const hoverIndex = action.payload.hoverIndex;
      const table = Object.assign({}, tables[sourceIndex]);
      tables.splice(sourceIndex, 1); // removing what you are dragging
      tables.splice(hoverIndex, 0, table); // inserting it into hoverIndex

      const tablesByUuid = {};
      let index = 0;
      // Need to update display orders of any changed
      tables.forEach((updateTable) => {
        const updatedTable = updateTable;
        updatedTable.display_order = index + 1;
        tablesByUuid[updatedTable.uuid] = { table: updatedTable, index };
        index += 1;
      });

      return Object.assign(
        {},
        state,
        { busy: false, tables, tablesByUuid }
      );
    }
    case ActionTypes.REORDER_SEATS: {
      const tables = state.tables.slice();
      const tablesByUuid = Object.assign({}, state.tablesByUuid);
      const tableObj = tablesByUuid[action.payload.tableUuid];
      if (tableObj == null || tableObj.table == null) {
        return Object.assign(
          {},
          state,
          { busy: false }
        );
      }
      const sourceIndex = action.payload.sourceIndex;
      const hoverIndex = action.payload.hoverIndex;
      const forceReorder = action.payload.forceReorder;

      const seatsReorderedTable = Object.assign({}, state.seatsReorderedTable);
      const table = forceReorder ? seatsReorderedTable : tableObj.table;
      const index = tableObj.index;

      const seatsByCoupleRole = Object.assign({}, state.seatsByGuestDistinction.couple);
      const seatsByGuestId = Object.assign({}, state.seatsByGuestDistinction.guests);

      const seats = table.seats.slice();
      const seat = Object.assign({}, seats[sourceIndex]);
      seats.splice(sourceIndex, 1); // removing dragged seat
      seats.splice(hoverIndex, 0, seat); // inserting into hoverIndex

      // Need to set the updated displayOrders
      const updatedSeats = [];
      seats.forEach((updateSeat) => {
        const updatedSeat = updateSeat;
        updatedSeat.display_order = updatedSeats.length + 1;
        updatedSeats.push(updatedSeat);
        if (updatedSeat.couple_role != null) {
          seatsByCoupleRole[updatedSeat.couple_role] = updatedSeat;
        } else if (updatedSeat.guest_id != null) {
          seatsByGuestId[updatedSeat.guest_id] = updatedSeat;
        }
      });

      table.seats = updatedSeats;
      tablesByUuid[table.uuid] = { table, index };
      tables[index] = table;

      const seatsByGuestDistinction = { guests: seatsByGuestId, couple: seatsByCoupleRole };

      return Object.assign(
        {},
        state,
        { busy: false, tables, tablesByUuid, seatsByGuestDistinction },
      );
    }
    case ActionTypes.REORDER_SEATS_IN_TABLE: {
      // Set the updateSeatsRequest with the reordered table seats and clear the seatsReorderedTable
      return Object.assign(
        {},
        state,
        { updateSeatsRequest: action.payload.table.seats, seatsReorderedTable: null },
      );
    }
    case ActionTypes.PUSH_SEATS_TO_TABLE: {
      const selectedGuests = state.selectedGuests;
      const tableUuid = action.payload.tableUuid;
      const guestsByGuestDistinction = action.payload.guestsByGuestDistinction;

      const tables = state.tables.slice();
      const tablesByUuid = Object.assign({}, state.tablesByUuid);
      let tableObj;
      let table;
      let index;
      const openSeats = {};
      if (tableUuid != null) { // if we aren't deleting seats
        tableObj = tablesByUuid[tableUuid];
        table = tableObj.table;
        index = tableObj.index;

        // Populate a list of open table seat uuids for the target table
        table.seats.forEach((tableSeat) => {
          if (tableSeat.isEmpty) {
            openSeats[tableSeat.uuid] = true;
          }
        });
      }

      const guestIds = selectedGuests.guests;
      const couple = selectedGuests.couple;
      const numSeats = guestIds.length + couple.length;
      // Return if no seats are moving or the target table exists and would overflow
      if (numSeats === 0 || (tableUuid != null && (numSeats > openSeats.length || openSeats.length === 0))) {
        return Object.assign(
          {},
          state,
          { busy: false }
        );
      }

      const seatsByCoupleRole = Object.assign({}, state.seatsByGuestDistinction.couple);
      const seatsByGuestId = Object.assign({}, state.seatsByGuestDistinction.guests);

      const shouldEmptySeats = {};
      const movedSeats = [];

      // Get seat information if the moving person is a couple member
      if (couple.length > 0) {
        couple.forEach((coupleRole) => {
          const movedSeat = {};
          const coupleMember = state.seatsByGuestDistinction.couple[coupleRole];
          if (coupleMember != null) { // Moving a seated guest, ensure that they will be "removed" from their previous table
            shouldEmptySeats[coupleMember.uuid] = coupleMember.table_uuid;
            movedSeat.display_name = coupleMember.display_name;
            movedSeat.table_uuid = coupleMember.table_uuid;
            delete seatsByCoupleRole[coupleRole];
          }
          movedSeat.couple_role = coupleRole;
          movedSeat.guest_id = null;
          movedSeats.push(movedSeat);
        });
      }

      // Get seat information if the moving person is a guest
      if (guestIds.length > 0) {
        guestIds.forEach((guestId) => {
          const movedSeat = {};
          const guest = state.seatsByGuestDistinction.guests[guestId];
          if (guest != null) { // Moving a seated guest, ensure that they will be "removed" from their previous table
            shouldEmptySeats[guest.uuid] = guest.table_uuid;
            movedSeat.display_name = guest.display_name;
            movedSeat.table_uuid = guest.table_uuid;
            delete seatsByGuestId[guestId];
          }
          movedSeat.couple_role = null;
          movedSeat.guest_id = guestId;
          movedSeats.push(movedSeat);
        });
      }

      let preventUpdate = false;
      const updateSeatsRequest = [];

      // Add the seats to the request and make sure we should update*
      // *We don't want to allow placement on the guest rail if any guests are already on guest rail / placement on table if any selected guest is already at the table

      movedSeats.forEach((seat) => {
        const guestFromTarget = seat.table_uuid === tableUuid || (seat.table_uuid == null && tableUuid == null);
        if (guestFromTarget) {
          preventUpdate = true;
        } else if (tableUuid != null) {
          updateSeatsRequest.push(seat);
        }
      });

      if (preventUpdate) {
        return Object.assign(
          {},
          state,
          { busy: false }
        );
      }

      let newSeatIdx = 0;
      const newSeats = [];
      if (tableUuid != null) { // If we're not deleting seats, update the existing seats in the target table with our new values
        let numFilledSeats = table.num_filled_seats;
        table.seats.forEach((seat) => {
          const newSeat = seat;
          if (openSeats[seat.uuid] != null && newSeatIdx < updateSeatsRequest.length) { // empty seat at target table, fill with new guest if we have one
            newSeat.isEmpty = false;

            const updateSeatDetails = updateSeatsRequest[newSeatIdx];
            // Update the seat with it's new guest/couple details
            newSeat.couple_role = updateSeatDetails.couple_role;
            newSeat.guest_id = updateSeatDetails.guest_id;
            if (updateSeatDetails.couple_role != null) {
              newSeat.display_name = guestsByGuestDistinction.couple[updateSeatDetails.couple_role].display_name;
              seatsByCoupleRole[newSeat.couple_role] = newSeat;
            } else {
              newSeat.display_name = guestsByGuestDistinction.guests[updateSeatDetails.guest_id].display_name;
              seatsByGuestId[newSeat.guest_id] = newSeat;
            }

            numFilledSeats += 1; // We added a seat, increment the number of filled seats
            updateSeatsRequest[newSeatIdx] = newSeat; // Update the seat in the request list with all the seat values
            newSeatIdx += 1;
          }
          newSeats.push(newSeat);
        });
        table.seats = newSeats;
        table.num_filled_seats = numFilledSeats;
        tables[index] = table;
        tablesByUuid[tableUuid] = { table, index };
      }

      // If any guests were moved from another table, we need to empty their previous seats
      if (!_.isEmpty(shouldEmptySeats)) {
        const emptySeats = Object.assign({}, shouldEmptySeats);
        Object.values(emptySeats).forEach((uuid) => {
          if (uuid != null) {
            const removeFromTableObj = tablesByUuid[uuid];
            const removeFromTable = removeFromTableObj.table;
            const removeFromTableIndex = removeFromTableObj.index;
            const updatedTableSeats = [];
            let numFilledSeats = removeFromTable.num_filled_seats;
            removeFromTable.seats.forEach((seat) => {
              const updatedSeat = seat;
              if (shouldEmptySeats[updatedSeat.uuid] != null) {
                updatedSeat.display_name = null;
                updatedSeat.guest_id = null;
                updatedSeat.couple_role = null;
                updatedSeat.isEmpty = true;
                delete shouldEmptySeats[updatedSeat.uuid];
                numFilledSeats -= 1;
                updateSeatsRequest.push(updatedSeat);
              }
              updatedTableSeats.push(updatedSeat);
            });
            removeFromTable.num_filled_seats = numFilledSeats;
            removeFromTable.seats = updatedTableSeats;
            tables[removeFromTableIndex] = removeFromTable;
            tablesByUuid[uuid] = { table: removeFromTable, index: removeFromTableIndex };
          }
        });
      }

      const seatsByGuestDistinction = { guests: seatsByGuestId, couple: seatsByCoupleRole };
      return Object.assign(
        {},
        state,
        { busy: false, tables, tablesByUuid, seatsByGuestDistinction, updateSeatsRequest },
      );
    }
    case ActionTypes.REMOVE_SEAT: {
      const seat = action.payload.seat;
      const tables = state.tables.slice();
      const tablesByUuid = Object.assign({}, state.tablesByUuid);
      const tableObj = tablesByUuid[seat.table_uuid];
      const table = tableObj.table;
      const index = tableObj.index;

      const seatsByCoupleRole = Object.assign({}, state.seatsByGuestDistinction.couple);
      const seatsByGuestId = Object.assign({}, state.seatsByGuestDistinction.guests);
      const updateSeatsRequest = [];

      if (seat.couple_role != null) {
        delete seatsByCoupleRole[seat.couple_role];
      } else {
        delete seatsByGuestId[seat.guest_id];
      }
      const seatsByGuestDistinction = { guests: seatsByGuestId, couple: seatsByCoupleRole };

      const tableSeats = [];
      table.seats.forEach((tableSeat) => {
        const updatedSeat = tableSeat;
        if (updatedSeat.uuid === seat.uuid) {
          updatedSeat.display_name = null;
          updatedSeat.guest_id = null;
          updatedSeat.couple_role = null;
          updatedSeat.isEmpty = true;
          updateSeatsRequest.push(updatedSeat);
        }
        tableSeats.push(updatedSeat);
      });

      table.seats = tableSeats;
      table.num_filled_seats -= 1;
      tables[index] = table;
      tablesByUuid[seat.table_uuid] = { table, index };

      return Object.assign(
        {},
        state,
        { busy: false, tables, tablesByUuid, seatsByGuestDistinction, updateSeatsRequest },
      );
    }
    case ActionTypes.TRIGGER_SEATING_ONBOARDING: {
      return Object.assign(
        {},
        state,
        { onboarding: true }
      );
    }

    case ActionTypes.FINISH_ONBOARDING: {
      return Object.assign(
        {},
        state,
        { onboarding: false, step: 0 }
      );
    }
    case ActionTypes.ONBOARDING_SELECTION_STEP: {
      return Object.assign(
        {},
        state,
        { onboarding: true, step: 0 }
      );
    }
    case ActionTypes.ONBOARDING_TABLE_STEP: {
      return Object.assign(
        {},
        state,
        { onboarding: true, step: 2 }
      );
    }
    case ActionTypes.CLEAR_SELECTED_GUESTS: {
      // clear the requests state and seatsReorderedTable
      // This is hit right before the update request is sent to the backend
      const selectedGuests = { guests: [], couple: [] };
      return Object.assign(
        {},
        state,
        { selectedGuests, updateSeatsRequest: [], seatsReorderedTable: null, draggingSeatInProgress: false, draggingTableInProgress: false, hoveredTable: null }
      );
    }
    case ActionTypes.INC_ONBOARDING_STEP: {
      const step = state.step + 1;
      return Object.assign({}, state, { step });
    }
    case ActionTypes.DEC_ONBOARDING_STEP: {
      const step = state.step - 1;
      return Object.assign({}, state, { step });
    }
    case ActionTypes.SET_NEW_CHART_EVENT: {
      const event = action.payload.event;
      return Object.assign({}, state, { event });
    }
    case ActionTypes.DELETE_CHART: {
      return Object.assign(
        {},
        initialState,
      );
    }
    case ActionTypes.SELECT_GUESTS: {
      const guest = action.payload.guest;
      if (guest == null) {
        return Object.assign(
          {},
          state,
          {}
        );
      }

      const selectedGuests = {};
      let guests = state.selectedGuests.guests.slice(); // Copy previous selected guests
      let couple = state.selectedGuests.couple.slice(); // Copy previous selected couple member
      if (action.payload.isCouple) {
        const coupleRole = guest.couple_role;
        const isChecked = _.includes(couple, coupleRole);
        if (isChecked) {
          couple = _.without(couple, coupleRole); // Remove the couple member from the selection
        } else {
          couple.push(coupleRole); // Add the couple member to the selection
        }
      } else {
        const guestId = guest.guest_view != null ? guest.guest_view.id : guest.guest_id; // Guest_View only exists from guest rail
        const isChecked = _.includes(guests, guestId);
        if (isChecked) {
          guests = _.without(guests, guestId); // Remove the guest from the selection
        } else {
          guests.push(guestId); // add the guest to the selection
          const guestGroup = action.payload.guestGroup;
          // If the guest group exists, we're coming from the guest rail
          if (guestGroup != null && guest.guest_view.relationship_type === 'PRIMARY') {
            // For primary guests in the guest rail, we want to select their secondary guests automatically
            for (let i = 0; i < guestGroup.guests.length; i += 1) {
              const isSeated = guestGroup.guests[i].table_uuid != null;
              const additionalGuest = guestGroup.guests[i].guest_view;
              if (additionalGuest != null) {
                const checked = _.includes(guests, additionalGuest.id);
                // If the secondary guest is not already checked, and not seated, add them to the selection
                if (!isSeated && !checked) {
                  guests.push(additionalGuest.id);
                }
              }
            }
          }
        }
      }

      selectedGuests.guests = guests;
      selectedGuests.couple = couple;

      return Object.assign(
        {},
        state,
        { selectedGuests }
      );
    }
    case ActionTypes.DRAG_STARTED: {
      const selectedGuests = Object.assign({}, state.selectedGuests);
      const guest = action.payload.guest;
      if (action.payload.isCouple) {
        const coupleRole = guest.couple_role;
        const isChecked = _.includes(selectedGuests.couple, coupleRole);
        if (!isChecked) {
          selectedGuests.couple.push(coupleRole); // Add the couple member to the selection
        }
      } else {
        const guestId = guest.guest_view != null ? guest.guest_view.id : guest.guest_id; // Guest_View only exists from guest rail
        const isChecked = _.includes(selectedGuests.guests, guestId);
        if (!isChecked) {
          selectedGuests.guests.push(guestId); // add the guest to the selection
        }
      }

      let seatsReorderedTable = null;
      let tableUuid = null;
      if (guest.uuid != null) {
        tableUuid = guest.table_uuid;
        // We need a deep copy
        seatsReorderedTable = tableUuid == null ? null : _.cloneDeep(state.tablesByUuid[tableUuid].table);
      }

      return Object.assign(
        {},
        state,
        { draggingSeatInProgress: true, selectedGuests, seatsReorderedTable, hoveredTable: tableUuid, draggingTableInProgress: false }
      );
    }
    case ActionTypes.DRAG_TABLE_STARTED: {
      return Object.assign(
        {},
        state,
        { draggingSeatInProgress: false, draggingTableInProgress: true }
      );
    }
    case ActionTypes.DRAG_ENDED: {
      return Object.assign(
        {},
        state,
        { draggingSeatInProgress: false, seatsReorderedTable: null, hoveredTable: null, draggingTableInProgress: false }
      );
    }
    case ActionTypes.SET_HOVERED_TABLE: {
      return Object.assign(
        {},
        state,
        { hoveredTable: action.payload.tableUuid }
      );
    }
    case ActionTypes.HIDE_SPINNER: {
      return Object.assign(
        {},
        state,
        { busy: false }
      );
    }
    default: return state;
  }
};

export default chartReducer;



// WEBPACK FOOTER //
// ./src/reducers/seatingChart.js